/* eslint-disable no-undef */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Typography } from '@pwskills/rachnaui';
import LeadFormOptionsApi from '@/api/leadForm';
import useUserDetails from '@/store/userDetails';
import {
  BackgroundFormOptionsTitle,
  BackgroundFormOptionsSlug,
  CourseInterestFormItem,
  CourseInterestFormSlug,
  ModalType,
  LeadCaptureEnquiryType,
} from '@/interfaces/leadCapture.enum';
import { IError, IUserPreferences } from '@/interfaces/common';
import { useUTMParameters } from '@pwskills/rachnaui/hooks';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { randomId } from '@pwskills/rachnaui/utils';
// import CategoryPageContext from '@/context/CategoryPageContext';
import CourseDescriptionContext from '@/context/CourseDescriptionContext';
import usePostLoginRedirection from '@/hooks/usePostLoginRedirection';
import AuthApi from '@/api/auth';
import styles from '@/styles/LeadFormModal2.module.css';
// import useIsCourseBought from '@/hooks/useIsCourseBought';
// import moEvent from '../../../utils/moEngage';
import LeadCaptureApi from '@/api/leadCapture';
import moEvent from '@/utils/moEngage';
import { updateUserInLocalStorage } from '@/utils/helper/helperFunction';
import { CategoryTypeEnums } from '@/GlobalEnums/enums';
import { domainNameEnums, saasDomains } from '@/utils/Domain';
import { domainName } from '@/utils/common/env.constants';
import Dropdown from './Dropdown';
import Stepper from './Stepper';
import Loader from './Loader';
import { GAevent } from '../../../utils/firebase.utils';
import {
  sendSkAuthenticationCompletedEvent,
  sendSkFormStartedEvent,
  sendSkFormSubmittedEvent,
} from '../../../utils/GAEventFunction';
import UserVerificationForm from './UserVerificationForm';

type LeadFormType = {
  buttonText: string;
  // type: string;
  modalType: string;
  courseName?: string;
  collegeName?: string;
  enquiryTypeNPF?: string;
  pageName?: string;
};

type OptionsType = {
  title: string;
  slug: string;
  categoryId: string;
};

const LeadFormModal2 = ({
  buttonText,
  // type,
  modalType,
  enquiryTypeNPF,
  courseName,
  collegeName,
  pageName,
}: LeadFormType) => {
  const AUTO_CLOSE_TIME = 3000;
  const utmQuery = useUTMParameters();
  const { goToDashoboard } = usePostLoginRedirection();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { courseDetails } = useContext(CourseDescriptionContext);
  const {
    userDetails: user,
    userIsLoggedIn: isLoggedIn,
    setUserDetails,
    setShowOnboarding,
  } = useUserDetails();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [contactDetails, setContactDetails] = useState({
    fullName: isLoggedIn ? `${user.firstName} ${user.lastName}` : '',
    email: isLoggedIn ? user.email : '',
    isdCode: (isLoggedIn && user.isdCode) || '',
    phoneNumber: isLoggedIn ? user.phoneNumber : '',
  });
  const [isFormTriggered, setIsFormTriggered] = useState<boolean>(false);
  const [studentDomainOptions, setStudentDomainOptions] = useState<{
    'Upskilling Course': OptionsType[];
    'Online Degree': OptionsType[];
  }>({
    'Upskilling Course': [],
    'Online Degree': [],
  });

  const [userPreferences, setUserPreferences] = useState<IUserPreferences>({
    background: { title: '', slug: '', categoryId: '' },
    interestedCourse: { title: '', slug: '', categoryId: '' },
    interestedDomain: { title: '', slug: '', categoryId: '' },
  });
  const [loading, setLoading] = useState<boolean>(false);
  const skFormStartedTrackedRef = useRef(false);

  const getUserPseudoId = () => {
    if (typeof document === 'undefined') return null;
    const gaCookie = document.cookie.split('; ').find((row) => row.startsWith('_ga='));
    if (!gaCookie) return null;
    const gaParts = gaCookie.split('.');
    if (gaParts.length > 2) {
      return `${gaParts[2]}.${gaParts[3]}`;
    }
    return null;
  };

  const getUserSegmentForAnalytics = () => {
    if (isLoggedIn) {
      return 'returning_user';
    }
    return 'new';
  };

  const getCourseContextForEvent = () => {
    const derivedCourseName = courseName || courseDetails?.title || courseDetails?.name || '';
    const derivedCourseId = courseDetails?._id || '';
    const derivedCategoryName = courseDetails?.categoryDetails?.title || '';
    const cohortId =
      typeof courseDetails?.cohortId === 'string'
        ? courseDetails?.cohortId
        : courseDetails?.cohortId?._id || '';
    const courseSlug = courseDetails?.slug || '';
    const price = courseDetails?.pricing?.IN ?? courseDetails?.pricing?.US;
    const discount = courseDetails?.pricing?.discount;
    const isFree = courseDetails?.pricing?.isFree;

    return {
      courseNameValue: derivedCourseName,
      courseIdValue: derivedCourseId,
      courseCategoryValue: derivedCategoryName,
      cohortIdValue: cohortId,
      courseSlugValue: courseSlug,
      priceValue: price,
      discountValue: discount,
      isFreeValue: isFree,
    };
  };

  const triggerSkFormSubmittedEvent = () => {
    const backgroundValue =
      userPreferences.background?.title ||
      user?.profileId?.userPreferences?.background?.title ||
      '';
    const courseInterestValue =
      userPreferences.interestedCourse?.title ||
      user?.profileId?.userPreferences?.interestedCourse?.title ||
      '';
    const domainValue =
      userPreferences.interestedDomain?.title ||
      user?.profileId?.userPreferences?.interestedDomain?.title ||
      '';
    const {
      courseNameValue,
      courseIdValue,
      courseCategoryValue,
      cohortIdValue,
      courseSlugValue,
      priceValue,
      discountValue,
      isFreeValue,
    } = getCourseContextForEvent();
    const pagePath = typeof window !== 'undefined' ? window.location.pathname : pageName || '';
    const formLocation = pageName || 'lead_form_modal_v2';
    const pageReferrerCta = buttonText || 'lead_form_modal_v2_submit';
    const widgetName = modalType || 'lead_form_modal_v2';
    const fallbackNameFromUser = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
    const sanitizedName = (contactDetails.fullName?.trim() || fallbackNameFromUser || '').trim();
    const sanitizedEmail = (contactDetails.email?.trim() || user?.email || '').trim();
    const fallbackPhoneNumber =
      contactDetails.phoneNumber || user?.phoneNumber || user?.primaryNumber || '';
    const sanitizedPhoneNumber = `${fallbackPhoneNumber || ''}`.trim();
    const fallbackPhoneCode = contactDetails.isdCode || user?.isdCode || user?.countryCode || '';
    const sanitizedPhoneCode = `${fallbackPhoneCode || ''}`.trim();

    sendSkFormSubmittedEvent({
      userData: user,
      isLoggedIn,
      user_account_type: user?.userType,
      page_path: pagePath,
      page_referrer_name: pageName,
      page_referrer_cta: pageReferrerCta,
      page_referrer_widget_name: widgetName,
      user_pseudo_id: getUserPseudoId() || undefined,
      pw_user_id: user?._id,
      user_segment: getUserSegmentForAnalytics(),
      form_id: 'lead_form_modal_v2',
      form_name: 'login form',
      form_type: 'lead',
      form_location: formLocation,
      form_total_fields: 3,
      background: backgroundValue || undefined,
      course_of_interest: courseInterestValue || undefined,
      domain: domainValue || undefined,
      course_name: courseNameValue || undefined,
      course_category: courseCategoryValue || undefined,
      course_id: courseIdValue || undefined,
      course_slug: courseSlugValue || undefined,
      cohort_id: cohortIdValue || undefined,
      price: priceValue,
      discount: discountValue,
      is_free: isFreeValue,
      user_name: sanitizedName || undefined,
      user_email: sanitizedEmail || undefined,
      user_phone_number: sanitizedPhoneNumber || undefined,
      user_phone_isd_code: sanitizedPhoneCode || undefined,
      name: sanitizedName || undefined,
      email: sanitizedEmail || undefined,
      phone_number: sanitizedPhoneNumber || undefined,
    });
  };

  const dropdownBackgroundOptions = [
    {
      title: BackgroundFormOptionsTitle.SCHOOL_STUDENT,
      slug: BackgroundFormOptionsSlug.SCHOOL_STUDENT,
      categoryId: '',
    },
    {
      title: BackgroundFormOptionsTitle.COLLEGE_STUDENT,
      slug: BackgroundFormOptionsSlug.COLLEGE_STUDENT,
      categoryId: '',
    },
    {
      title: BackgroundFormOptionsTitle.GRADUATE,
      slug: BackgroundFormOptionsSlug.GRADUATE,
      categoryId: '',
    },
    {
      title: BackgroundFormOptionsTitle.WORKING_PROFESSIONAL,
      slug: BackgroundFormOptionsSlug.WORKING_PROFESSIONAL,
      categoryId: '',
    },
  ];

  const dropDownCourseIntereseOptions = [
    {
      title: CourseInterestFormItem.UPSKILLING_COURSE,
      slug: CourseInterestFormSlug.UPSKILLING_COURSE,
      categoryId: '',
    },
    {
      title: CourseInterestFormItem.ONLINE_DEGREE,
      slug: CourseInterestFormSlug.ONLINE_DEGREE,
      categoryId: '',
    },
  ];

  const isDisabled = useMemo(() => {
    const isBackgroundSelected = userPreferences.background.slug !== '';
    const isInterestedCourseSelected = userPreferences.interestedCourse.slug !== '';
    const isInterestedDomainSelected = userPreferences.interestedDomain.slug !== '';

    if (currentStep === 1) {
      return isInterestedCourseSelected && isBackgroundSelected;
    }
    return isInterestedCourseSelected && isBackgroundSelected && isInterestedDomainSelected;
  }, [userPreferences, currentStep]);

  const resetForm = () => {
    setUserPreferences({
      background: { title: '', slug: '', categoryId: '' },
      interestedCourse: { title: '', slug: '', categoryId: '' },
      interestedDomain: { title: '', slug: '', categoryId: '' },
    });
  };

  function getObjDataDb(data: any) {
    const dbData = {
      ...(isLoggedIn && { user_id: user?._id }),
      user_type: isLoggedIn ? ('logged_in' as const) : ('logged_out' as const),
      phone_number: data.phoneNumber || user.phoneNumber || '',
      username: `${user?.firstName} ${user?.lastName}` || '',
      student_background:
        userPreferences?.background?.title || user?.profileId.userPreferences?.background.title,
      domain_interest:
        userPreferences?.interestedCourse?.title ||
        user?.profileId.userPreferences?.interestedCourse.title,
      category_name:
        userPreferences?.interestedDomain?.title ||
        user?.profileId.userPreferences?.interestedDomain.title,
      ...(user?.verified && user?.email && { email: user.email }),
      ...(modalType === ModalType.ONLINE_DEGREE &&
        {
          // background: selectedBackground,
          // experience: selectedWorkExperience,
        }),
      page_name: pageName,
      date_time: new Date().toISOString(),
    };

    return {
      ...dbData,
      course_name: courseName || (courseDetails && courseDetails?.title),
      // course_id: courseDetails && courseDetails._id,
      // category_name: courseDetails && courseDetails?.categoryDetails?.title,
      // category_id: courseDetails && courseDetails?.categoryDetails?._id,
      enquiry_type: enquiryTypeNPF,
    };
  }

  const sendDataToDB = async (data: any) => {
    const objData = getObjDataDb(data);
    try {
      await LeadCaptureApi.sendDataToDatabase(objData);
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.error((error as IError)?.error);
    }
  };

  const sendGAEvent = (clickText: string) => {
    const GAEventDataObj = {
      user_id: user?._id,
      user_type: 'loggedin',
      user_category: user?.userType || '',
      student_background: userPreferences?.background?.title,
      domain_interest: userPreferences?.interestedCourse?.title,
      category_name: userPreferences?.interestedDomain?.title,
      enquiryType: clickText,
      method: isLoggedIn ? 'otp' : '',
      source: '',
      page_name: pageName,
      utm_source: utmQuery?.utm_source || '',
      utm_medium: utmQuery?.utm_medium || '',
      utm_campaign: utmQuery?.utm_campaign || '',
    };
    GAevent('sk_form_submit', GAEventDataObj);
  };

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      return '';
    }

    try {
      const token = await executeRecaptcha(randomId());
      return token;
    } catch (error) {
      return '';
    }
  }, [executeRecaptcha]);

  const sendMoengageEvent = (data: any) => {
    const moEventDataObj = {
      user_id: user?._id,
      user_mobile: user?.phoneNumber,
      last_name: user?.lastName || '',
      first_name: user?.firstName || '',
      email: user?.email || '',
      username: `${user?.firstName} ${user?.lastName}`,
      student_background: data?.userPreferences.background.title,
      student_interest: data?.userPreferences.interestedCourse.title,
      category_name: data?.userPreferences.interestedDomain.title,
    };
    moEvent('onboarding_form_submit', moEventDataObj);
  };

  const sendLead = async (data: any) => {
    const token = await handleReCaptchaVerify();
    // const GAEventDataObj = getEventDataObj(data, 'GA');
    // GAevent('sk_form_submit', GAEventDataObj);
    sendDataToDB(data);
    sendGAEvent(LeadCaptureEnquiryType.FOOTER_LEAD_FORM);
    sendMoengageEvent(data);

    try {
      const queryParams = [];
      if (data.query?.source) queryParams.push(`source=${encodeURIComponent(data.query.source)}`);
      if (data.query?.medium) queryParams.push(`medium=${encodeURIComponent(data.query.medium)}`);
      if (data.query?.campaign)
        queryParams.push(`campaign=${encodeURIComponent(data.query.campaign)}`);
      if (data.query?.enquiryType)
        queryParams.push(`enquiryType=${encodeURIComponent(data.query.enquiryType)}`);
      if (data.query?.field_course_new)
        queryParams.push(`field_course_new=${encodeURIComponent(data.query.field_course_new)}`);
      if (data.query?.field_course_type)
        queryParams.push(`field_course_type=${encodeURIComponent(data.query.field_course_type)}`);

      let queryString = '';
      if (queryParams.length > 0) {
        queryString += `?${queryParams.join('&')}`;
      }
      const userData = {
        ...user,
        profileId: {
          ...user.profileId,
          userPreferences: {
            ...user.profileId.userPreferences,
            ...data.userPreferences,
          },
        },
      };
      setUserDetails(userData);
      updateUserInLocalStorage(userData);
      await AuthApi.saveUserPreferences(data.userPreferences, queryString, token);
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.error((error as IError)?.error);
    } finally {
      setIsFormTriggered(false);
      resetForm();
      setTimeout(() => {
        setLoading(false);
        goToDashoboard();
      }, AUTO_CLOSE_TIME);
    }
  };

  const handleSendLead = async () => {
    setLoading(true);
    const leadData = {
      userPreferences,
      query: {
        source: utmQuery?.utm_source,
        medium: utmQuery?.utm_medium,
        campaign: utmQuery?.utm_campaign,
        enquiryType: enquiryTypeNPF,
        field_course_new: courseDetails && courseDetails?.title,
        field_course_type: courseDetails && courseDetails?.courseType,
        ...(pageName === 'course_page' && {
          field_course_new: courseDetails && courseDetails?.title,
        }),
        ...(modalType === ModalType.ONLINE_DEGREE && {
          field_course_new: courseName || (courseDetails && courseDetails?.title),
        }),
        ...(modalType === ModalType.ONLINE_DEGREE && {
          field_course_type: 'online_degree',
          field_college_name:
            collegeName ||
            (courseDetails &&
              courseDetails?.partnerInstituteDetails &&
              courseDetails?.partnerInstituteDetails[0]?.name),
          field_website_page_visit: 'Online Degree',
        }),
      },
    };
    triggerSkFormSubmittedEvent();

    const {
      courseNameValue,
      courseIdValue,
      courseCategoryValue,
      cohortIdValue,
      courseSlugValue,
      priceValue,
      discountValue,
      isFreeValue,
    } = getCourseContextForEvent();
    const pagePath = typeof window !== 'undefined' ? window.location.pathname : pageName || '';
    const fallbackNameFromUser = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
    const sanitizedName = (contactDetails.fullName?.trim() || fallbackNameFromUser || '').trim();
    const sanitizedEmail = (contactDetails.email?.trim() || user?.email || '').trim();
    const fallbackPhoneNumber =
      contactDetails.phoneNumber || user?.phoneNumber || user?.primaryNumber || '';
    const sanitizedPhoneNumber = `${fallbackPhoneNumber || ''}`.trim();

    sendSkAuthenticationCompletedEvent({
      userData: user,
      isLoggedIn,
      user_account_type: user?.userType,
      page_path: pagePath,
      page_referrer_cta: buttonText || 'lead_form_modal_v2_submit',
      page_referrer_widget_name: modalType || 'lead_form_modal_v2',
      user_pseudo_id: getUserPseudoId() || undefined,
      pw_user_id: user?._id,
      user_segment: getUserSegmentForAnalytics(),
      form_id: 'lead_form_modal_v2',
      form_name: 'login form',
      form_type: 'lead',
      form_location: pageName || 'lead_form_modal_v2',
      form_total_fields: 3,
      login_method: 'otp',
      login_result: isLoggedIn ? 'signin' : 'signup',
      user_name: sanitizedName || undefined,
      user_phone_number: sanitizedPhoneNumber || undefined,
      user_email: sanitizedEmail || undefined,
      course_batch_name: courseNameValue || undefined,
      course_batch_slug: courseSlugValue || undefined,
      course_batch_category: courseCategoryValue || undefined,
      course_batch_id: courseIdValue || undefined,
      course_batch_cohort_id: cohortIdValue || undefined,
      price: priceValue,
      discount_percentage: discountValue,
      is_free: isFreeValue,
      platform: 'web',
    });

    sendLead(leadData);
  };

  const handleSelectChange = (
    id: string,
    { title, slug, categoryId }: { title: string; slug: string; categoryId: string }
  ): void => {
    setUserPreferences((prevData: any) => ({ ...prevData, [id]: { title, slug, categoryId } }));
  };

  const handleCurrentStep = (step: number) => {
    if (!isDisabled && currentStep < step) return;
    if (step !== 3) setCurrentStep(step);
  };

  const renderDropdown = (step: number) => {
    switch (step) {
      case 1:
        return (
          <div className="flex flex-col gap-4">
            <Dropdown
              options={dropdownBackgroundOptions}
              value={(userPreferences.background as OptionsType) || ''}
              onChange={(title, slug, categoryId) =>
                handleSelectChange('background', { title, slug, categoryId })
              }
              label="Tell us about your background?"
              placeholder="Select Background"
              className="cursor-pointer"
              dropDownType="default"
            />
            <Dropdown
              options={dropDownCourseIntereseOptions}
              value={(userPreferences.interestedCourse as OptionsType) || ''}
              onChange={(title, slug, categoryId) =>
                handleSelectChange('interestedCourse', { title, slug, categoryId })
              }
              label="What type of course are you interested in?"
              placeholder="Select Course"
              className="cursor-pointer"
              dropDownType="default"
            />
          </div>
        );
      case 2:
        return (
          <Dropdown
            options={
              studentDomainOptions[
                userPreferences.interestedCourse.title as keyof typeof studentDomainOptions
              ] || []
            }
            value={(userPreferences.interestedDomain as OptionsType) || ''}
            onChange={(title, slug, categoryId) =>
              handleSelectChange('interestedDomain', { title, slug, categoryId })
            }
            label="Which domain interests you the most?"
            placeholder="Select Domain"
            className="cursor-pointer"
            dropDownType="default"
          />
        );
      case 3:
        return (
          <UserVerificationForm
            contactDetails={contactDetails}
            setContactDetails={setContactDetails}
          />
        );
      default:
        return null;
    }
  };

  const handleNext = () => {
    if (isLoggedIn && currentStep > 1) {
      setCurrentStep(1);
      handleSendLead();
      // setIsFormTriggered(true);
    } else {
      setIsFormTriggered(true);
      setShowOnboarding(false);
      setCurrentStep((prevStep) => prevStep + 1);
    }
  };

  useEffect(() => {
    if (skFormStartedTrackedRef.current) return;

    const pagePath = typeof window !== 'undefined' ? window.location.pathname : pageName || '';
    const courseNameValue = courseName || courseDetails?.title || courseDetails?.name || '';
    const courseIdValue = courseDetails?._id || '';
    const courseCategoryValue = courseDetails?.categoryDetails?.title || '';
    const cohortIdValue =
      typeof courseDetails?.cohortId === 'string'
        ? courseDetails?.cohortId
        : courseDetails?.cohortId?._id || '';
    const courseSlugValue = courseDetails?.slug || '';
    const priceValue = courseDetails?.pricing?.IN ?? courseDetails?.pricing?.US;
    const discountValue = courseDetails?.pricing?.discount;
    const isFreeValue = courseDetails?.pricing?.isFree;

    sendSkFormStartedEvent({
      userData: user,
      isLoggedIn,
      user_account_type: user?.userType,
      page_path: pagePath,
      page_referrer_name: pageName,
      page_referrer_cta: buttonText || 'lead_form_modal_v2_start',
      page_referrer_widget_name: modalType || 'lead_form_modal_v2',
      user_pseudo_id: getUserPseudoId() || undefined,
      pw_user_id: user?._id,
      user_segment: isLoggedIn ? 'returning_user' : 'new',
      form_id: 'lead_form_modal_v2',
      form_name: 'Lead Form',
      form_type: 'lead',
      form_location: pageName || 'lead_form_modal_v2',
      form_total_fields: 3,
      form_time_fill_sec: 0,
      course_batch_name: courseNameValue || undefined,
      course_batch_slug: courseSlugValue || undefined,
      course_batch_category: courseCategoryValue || undefined,
      course_batch_id: courseIdValue || undefined,
      course_batch_cohort_id: cohortIdValue || undefined,
      price: priceValue,
      discount_percentage: discountValue,
      is_free: isFreeValue,
    });

    skFormStartedTrackedRef.current = true;
  }, [
    buttonText,
    courseDetails?._id,
    courseDetails?.categoryDetails?.title,
    courseDetails?.cohortId,
    courseDetails?.name,
    courseDetails?.pricing?.IN,
    courseDetails?.pricing?.US,
    courseDetails?.pricing?.discount,
    courseDetails?.pricing?.isFree,
    courseDetails?.slug,
    courseDetails?.title,
    courseName,
    isLoggedIn,
    modalType,
    pageName,
    user,
  ]);

  useEffect(() => {
    if (isLoggedIn && isFormTriggered) {
      handleSendLead();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [upskillResponse, degreeResponse] = await Promise.all([
          LeadFormOptionsApi.getCourseList({
            categoryType: saasDomains.includes(domainName as domainNameEnums)
              ? CategoryTypeEnums.YCMOU
              : CategoryTypeEnums.SKILLS,
          }),
          LeadFormOptionsApi.getCourseList({
            categoryType: saasDomains.includes(domainName as domainNameEnums)
              ? CategoryTypeEnums.YCMOU
              : CategoryTypeEnums.SKILLS_ONLINE_DEGREE,
          }),
        ]);

        setStudentDomainOptions({
          'Upskilling Course': upskillResponse.data.map((d) => ({
            title: d.title,
            slug: d.slug,
            categoryId: d.categoryId,
          })),
          'Online Degree': degreeResponse.data.map((d) => ({
            title: d.title,
            slug: d.slug,
            categoryId: d.categoryId,
          })),
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching data for step 3:', error);
      } finally {
        setLoading(false);
      }
    };
    if (
      studentDomainOptions[dropDownCourseIntereseOptions[0].title].length === 0 ||
      studentDomainOptions[dropDownCourseIntereseOptions[1].title].length === 0
    ) {
      fetchData();
    }
  }, []);

  if (loading) return <Loader />;

  return (
    <div className="w-full flex justify-center items-center rounded-lg shadow-lg overflow-hidden bg-white pt-6 medium:pt-10">
      {/* <Loader /> */}
      <div className="w-full max-w-[1440px]">
        <div className="large:min-h-[500px] flex flex-col medium:flex-row justify-between w-[90%] bg-gradient-to-t medium:bg-gradient-to-l from-[#FFE5E5] from-70% to-[#FFC6BB] rounded-t-xl shadow-lg m-auto border border-r-[#FFDBD3] border-t-[#FFDBD3] border-l-[#FFDBD3]">
          {/* Left Section */}
          <div className="relative h-auto medium:max-w-[200px] large:!max-w-[28%] flex flex-col justify-between">
            <div className="absolute left-0 top-0">
              <img
                src="/images/accountImage/leadform/Rings.svg"
                alt="ellipse-bg"
                className="opacity-15 hidden medium:!block"
              />
            </div>
            <div className="absolute w-6 h-6 left-8 top-[20%] medium:top-[5%] large:top-5  medium:left-4">
              <img
                src="/images/accountImage/leadform/Dots.svg"
                alt="arrow-bg"
                className="w-6 medium:w-[17px] h-auto object-contain"
              />
            </div>
            <div className="absolute w-40 h-40 top-0 -right-4 medium:hidden">
              <img
                src="/images/accountImage/leadform/SkillsEmboss.svg"
                alt="arrow-bg"
                className="absolute w-full h-full object-contain"
              />
            </div>
            <div className="absolute w-48 h-48 hidden medium:!block medium:bottom-0 medium:-left-4">
              <img
                src="/images/accountImage/leadform/SkillsEmboss.svg"
                alt="arrow-bg"
                className="absolute w-full h-full object-contain"
              />
            </div>
            <img
              src="/images/accountImage/leadform/question-mark.svg"
              alt="Question Mark"
              className="absolute block top-0 right-0 medium:!hidden w-20 h-20"
            />
            <div className="flex flex-col p-8 mt-4 z-10 medium:p-4 large:p-8 medium:mt-0">
              <Typography
                variant="bold"
                component="h2"
                className="text-gray-850 mb-2 fint-inter font-bold text-2xl medium:text-xl large:!text-[32px] leading-[36px] medium:leading-[30px] large:leading-[40px]"
              >
                Need Help?
              </Typography>
              <Typography
                variant="medium"
                component="body-tiny"
                className="!text-sm text-[#3D3D3D] font-nunito !leading-[22px] font-normal w-[90%] medium:!w-full"
              >
                Connect with us & know what&apos;s best for you.
              </Typography>
            </div>

            <div className="flex flex-col justify-between z-10">
              <div className="relative flex flex-col justify-end items-end h-full">
                <img
                  src="/images/accountImage/leadform/question-mark.svg"
                  alt="Question Mark"
                  className={`relative hidden top-4 large:top-8 medium:!block medium:right-[3%] large:right-[10%] w-20 h-20 medium:w-[60px] medium:h-[60px] large:!w-20 large:!h-20   ${styles.bounceQuestionMarkIcon}`}
                  // style={{ animation: 'bounceQuestionMarkIcon 3s infinite' }}
                />
                <img
                  src="/images/accountImage/leadform/man.webp"
                  alt="Thinking Man"
                  className="hidden w-[600px] relative medium:!block medium:bottom-0 large:!w-[400px]"
                />
              </div>
            </div>
          </div>

          {/* Right Section */}
          <div className="medium:max-w-[650px] large:!max-w-full flex flex-col w-full h-full justify-center items-end py-8 px-4 gap-4 medium:gap-6 large:!pr-20 large:!gap-10">
            <Stepper currentStep={currentStep - 1} handleCurrentStep={handleCurrentStep} />
            <div className="w-full flex flex-col justify-start gap-4 large:!gap-6 bg-[#ffffff] large:max-w-[650px] rounded-lg large:!rounded-md  shadow-lg p-4 medium:px-3 medium:py-4 large:!p-6">
              {renderDropdown(currentStep)}
              <div className="flex justify-center items-center">
                {currentStep < 3 && (
                  <Button
                    className="text-white text-sm font-semibold leading-[22px] pt-2 pb-3 px-5 rounded-md medium:max-w-[110px] w-full large:max-w-[150px] medium:!px-[15px] medium:!py-[8px] large:!px-[20px] large:!py-[9px]"
                    onClick={() => handleNext()}
                    disabled={!isDisabled}
                  >
                    {buttonText}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadFormModal2;
